/**
 * Boat Service - API client per la gestione delle barche
 */

import { apiFetch, API_URL } from './apiFetch';

export interface BoatExtra {
  id: string;
  custom_price?: number;
}

export interface Boat {
  id: string;
  owner_id: string;
  /** Stato approvazione admin: PENDING_REVIEW | APPROVED | REJECTED. */
  approval_status?: string;
  /** Offerta a locazione (bareboat). false = barca solo per escursioni/esperienze. */
  bareboat_enabled?: boolean;
  /** Barca usata SOLO come mezzo per escursioni/esperienze (non a noleggio). */
  excursion_only?: boolean;
  /** Nº escursioni che usano questa barca + flag dual-use (da /boats/my). */
  excursion_count?: number;
  used_in_excursions?: boolean;
  /** SEO slug univoco usato come URL identifier (es. "predator-500-villasimius-d5d0"). */
  slug?: string;
  name: string;
  description?: string;
  boat_type?: string;
  length_meters?: number;
  max_capacity?: number;
  /** Posti prenotabili dai clienti (ospiti). null/undefined → fallback max_capacity. */
  max_guests?: number | null;
  year_built?: number;
  // Location fields
  location?: string;      // Legacy field
  city?: string;          // City for filtering (e.g., Olbia, Alghero)
  zone?: string;          // Geographic zone (e.g., Costa Smeralda)
  port_name?: string;     // Marina/port name (e.g., Marina di Olbia)
  pier?: string;          // Specific pier/dock (e.g., Molo 3)
  marina_address?: string;
  port_id?: string | null; // FK to pois (category=marina) — drives external services matching
  mooring_id?: string | null; // FK to port_moorings — ormeggio specifico (banchina/boa/pontile)
  latitude?: number;      // GPS latitude
  longitude?: number;     // GPS longitude
  // Pricing
  price_per_day?: number;
  price_half_day?: number;
  price_per_week?: number;
  price_per_month?: number;
  partial_billing_mode?: string;
  last_minute_enabled?: boolean;
  last_minute_days_before?: number | null;
  last_minute_discount_percent?: number | null;
  price_per_hour?: number;
  // Range pricing (presente solo se richiesto con start_date+end_date)
  price_for_range_total?: number;
  price_for_range_avg?: number;
  price_for_range_days?: number;
  season_from_price?: number;
  season_from_unit?: 'half_day' | 'day' | 'week' | 'month';
  /** Mezza giornata (min) della stagione in corso — mostrata "in piccolo" nella card. */
  season_half_price?: number;
  /** Azienda "stesso prezzo per tutti i punti" (false = per-porto). */
  departure_same_price?: boolean;
  // Services
  skipper_available?: boolean;
  skipper_required?: boolean;
  hostess_available?: boolean;
  skipper_price_per_day?: number;
  fuel_included?: boolean;
  min_rental_hours?: number;
  min_rental_days?: number;
  max_rental_days?: number;
  rental_mode?: string;
  /** #11 override per-barca modalità incasso: 'inherit_vendor' | 'full' | 'commission_only'. */
  payment_collection_mode?: string | null;
  deposit_percent?: number | null;
  /** #11 modalità incasso EFFETTIVA (boat→vendor), esposta da /api/boats/[id] (camelCase). */
  paymentCollectionMode?: string;
  opening_time?: string | null;
  closing_time?: string | null;
  switch_time?: string | null;
  break_minutes?: number | null;
  rental_hours_override?: boolean | null;
  changeover_day?: number;
  changeover_checkin_time?: string;
  changeover_checkout_time?: string;
  short_stay_surcharge?: number;
  engine_type?: string;
  engine_power_hp?: number;
  engine_count?: number;
  engine_powers?: number[];
  fuel_type?: string;
  fuel_capacity_liters?: number;
  water_capacity_liters?: number;
  max_speed_knots?: number;
  cruising_speed_knots?: number;
  requires_license?: boolean;
  license_type?: string;
  // Specs
  cabins?: number;
  berths?: number;
  bathrooms?: number;
  beam_meters?: number;
  draft_meters?: number;
  manufacturer?: string;
  model?: string;
  fuel_not_included?: boolean;
  security_deposit?: number;
  image_urls?: string[];
  cover_image_index?: number;
  bookings_suspended?: boolean;
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'ARCHIVED';
  created_at?: string;
  updated_at?: string;
  // Relations
  owner?: {
    id: string;
    name: string;
    email: string;
    avatar_url?: string;
    role: 'VENDOR' | 'COMPANY' | 'ADMIN';
    company_name?: string;
  };
  amenities?: any[];
  extras?: BoatExtra[];
  reviews?: any[];
  rating?: string | number;
  reviews_count?: number;
  cancellation_policies?: {
    id: number;
    name: string;
    slug: string;
    description?: string | null;
    tiers?: any[];
  } | null;
  // Stagioni (markupped dal backend) per scheda catalogo: tariffa "in corso" + tabella.
  seasonalPrices?: Array<{
    name?: string;
    start_date?: string;
    end_date?: string;
    start_md?: string | null;
    end_md?: string | null;
    price_per_day?: number;
    price_half_day?: number | null;
    is_active?: boolean;
  }>;
  departure_points?: { id: string; name: string; lat: number; lng: number; is_default: boolean;
    priced?: boolean; from_price?: number | null; from_unit?: 'half_day' | 'day' | 'week' | 'month' | null; half_price?: number | null }[];
}

export interface BoatFilters {
  status?: string;
  ownerId?: string;
  /** UUID hub dell'owner: scoping catalogo a UNA company (es. QR agenzia → solo Sailing). */
  hubOwnerId?: string;
  minPrice?: number;
  maxPrice?: number;
  boatType?: string;
  minCapacity?: number;
  // Range disponibilità (ISO YYYY-MM-DD). Se forniti, il backend esclude
  // dalla risposta le barche con CLOSED/NOT_AVAILABLE, blocked_dates o
  // bookings PENDING/CONFIRMED che si sovrappongono al range.
  from?: string;
  to?: string;
}

// Trasforma i dati dal backend al formato frontend
const transformBoat = (boat: any): Boat => ({
  id: boat.id,
  owner_id: boat.owner_id,
  name: boat.name,
  description: boat.description,
  boat_type: boat.boat_type,
  length_meters: boat.length_meters,
  max_capacity: boat.max_capacity,
  max_guests: boat.max_guests,
  year_built: boat.year_built,
  // Location fields
  location: boat.location,
  city: boat.city,
  zone: boat.zone,
  port_name: boat.port_name,
  pier: boat.pier,
  marina_address: boat.marina_address,
  port_id: boat.port_id ?? null,
  mooring_id: boat.mooring_id ?? null,
  latitude: parseFloat(boat.latitude) || undefined,
  longitude: parseFloat(boat.longitude) || undefined,
  // Pricing
  price_per_day: parseFloat(boat.price_per_day) || 0,
  price_half_day: parseFloat(boat.price_half_day) || 0,
  price_per_week: boat.price_per_week != null ? parseFloat(boat.price_per_week) : undefined,
  price_per_month: boat.price_per_month != null ? parseFloat(boat.price_per_month) : undefined,
  partial_billing_mode: boat.partial_billing_mode || 'pro_rata',
  // Range pricing (presente solo se /api/boats riceve start_date+end_date)
  price_for_range_total: boat.price_for_range_total != null ? parseFloat(boat.price_for_range_total) : undefined,
  price_for_range_avg: boat.price_for_range_avg != null ? parseFloat(boat.price_for_range_avg) : undefined,
  price_for_range_days: boat.price_for_range_days as number | undefined,
  // Prezzo "da" stagionale (stagione in corso, unità piena + markup) — card catalogo.
  season_from_price: boat.season_from_price != null ? parseFloat(String(boat.season_from_price)) : undefined,
  season_from_unit: boat.season_from_unit as Boat['season_from_unit'],
  season_half_price: boat.season_half_price != null ? parseFloat(String(boat.season_half_price)) : undefined,
  departure_same_price: boat.departure_same_price,
  // Services
  skipper_available: boat.skipper_available,
  skipper_required: boat.skipper_required,
  hostess_available: boat.hostess_available,
  skipper_price_per_day: parseFloat(boat.skipper_price_per_day) || 0,
  fuel_included: boat.fuel_included,
  min_rental_hours: boat.min_rental_hours,
  min_rental_days: boat.min_rental_days,
  max_rental_days: boat.max_rental_days,
  rental_mode: boat.rental_mode || 'daily',
  opening_time: boat.opening_time ?? null,
  closing_time: boat.closing_time ?? null,
  switch_time: boat.switch_time ?? null,
  break_minutes: boat.break_minutes ?? null,
  rental_hours_override: boat.rental_hours_override ?? null,
  changeover_day: boat.changeover_day,
  changeover_checkin_time: boat.changeover_checkin_time,
  changeover_checkout_time: boat.changeover_checkout_time,
  short_stay_surcharge: boat.short_stay_surcharge ? parseFloat(String(boat.short_stay_surcharge)) : undefined,
  engine_type: boat.engine_type ?? undefined,
  engine_power_hp: boat.engine_power_hp,
  engine_count: boat.engine_count ?? undefined,
  engine_powers: boat.engine_powers ?? undefined,
  fuel_type: boat.fuel_type ?? undefined,
  fuel_capacity_liters: boat.fuel_capacity_liters ?? undefined,
  water_capacity_liters: boat.water_capacity_liters ?? undefined,
  max_speed_knots: boat.max_speed_knots ?? undefined,
  cruising_speed_knots: boat.cruising_speed_knots ?? undefined,
  requires_license: boat.requires_license,
  license_type: boat.license_type,
  // Specs
  cabins: boat.cabins || undefined,
  berths: boat.berths || undefined,
  bathrooms: boat.bathrooms || undefined,
  beam_meters: boat.beam_meters ? parseFloat(boat.beam_meters) : undefined,
  draft_meters: boat.draft_meters ? parseFloat(boat.draft_meters) : undefined,
  manufacturer: boat.manufacturer || undefined,
  model: boat.model || undefined,
  fuel_not_included: boat.fuel_not_included || false,
  security_deposit: boat.security_deposit ? parseFloat(boat.security_deposit) : undefined,
  image_urls: boat.image_urls || [],
  cover_image_index: boat.cover_image_index || 0,
  status: boat.status,
  created_at: boat.created_at,
  updated_at: boat.updated_at,
  approval_status: boat.approval_status,
  bareboat_enabled: boat.bareboat_enabled,
  excursion_only: boat.excursion_only,
  excursion_count: boat.excursion_count,
  used_in_excursions: boat.used_in_excursions,
  // Relations - pass through as-is from backend
  owner: boat.owner,
  amenities: boat.amenities,
  extras: boat.extras,
  reviews: boat.reviews,
  rating: boat.rating,
  reviews_count: boat.reviews_count,
  cancellation_policies: boat.cancellation_policies ?? null,
  // Stagioni (markupped dal backend) — servono alla scheda catalogo per mostrare
  // la tariffa "in corso" e la tabella stagionalità. Passthrough as-is.
  seasonalPrices: ((boat as { seasonalPrices?: Boat['seasonalPrices'] }).seasonalPrices) ?? [],
  departure_points: boat.departure_points ?? [],
});

export const boatService = {
  // Get my boats (owner-scoped, for admin/vendor dashboards)
  async getMyBoats(): Promise<Boat[]> {
    const data = await apiFetch('/boats/my');
    return (data.data || []).map(transformBoat);
  },

  // Get all boats with optional filters.
  // Phase 9-pre D: resta su legacy /api/boats. Filtri complessi (date range
  // pricing, availability, cutoff, search-combinations) non ancora portati
  // su /api/listings GET. Dual-write garantisce coerenza listings table.
  // Cutover Phase 9 farà swap atomico dopo arricchimento /api/listings.
  async getBoats(filters: BoatFilters & { startDate?: string; endDate?: string } = {}): Promise<Boat[]> {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.ownerId) params.append('ownerId', filters.ownerId);
    if (filters.hubOwnerId) params.append('hub_owner_id', filters.hubOwnerId);
    if (filters.minPrice) params.append('minPrice', filters.minPrice.toString());
    if (filters.maxPrice) params.append('maxPrice', filters.maxPrice.toString());
    if (filters.boatType) params.append('boatType', filters.boatType);
    if (filters.minCapacity) params.append('minCapacity', filters.minCapacity.toString());
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    // Range pricing (separato dal filtro disponibilità): trigger calcolo
    // priceForRangeTotal lato backend, no filtro disp.
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);

    const data = await apiFetch(`/boats?${params}`);
    return (data.data || []).map(transformBoat);
  },

  // Get single boat by ID.
  // Optional startDate/endDate per applicare seasonal/override per il range
  // selezionato dal cliente. Senza date: solo base price_per_day esposto.
  async getBoatById(id: string, opts?: { startDate?: string; endDate?: string }): Promise<Boat> {
    const params = new URLSearchParams();
    if (opts?.startDate) params.append('startDate', opts.startDate);
    if (opts?.endDate) params.append('endDate', opts.endDate);
    const qs = params.toString();
    const data = await apiFetch(`/boats/${id}${qs ? '?' + qs : ''}`);
    return transformBoat(data.data);
  },

  // Create new boat — Phase 9-pre A: via strangler proxy /api/listings.
  // Endpoint POST /api/listings dispatch a POST /api/boats internamente +
  // dual-write a tabella listings. Wizard riceve boat.id (= source_id) per
  // upload images successivi. Trasparente per consumer esistenti.
  async createBoat(boatData: Partial<Boat>): Promise<Boat> {
    const data = await apiFetch('/listings', {
      method: 'POST',
      body: JSON.stringify({ ...boatData, type: 'bareboat' }),
    });
    return transformBoat(data.data);
  },

  // Update boat — Phase 9-pre A: proxy via /api/listings/[id].
  // Endpoint risolve source_boat_id internamente e dispatch a PUT /api/boats/[id].
  // Per ora teniamo legacy direct (più veloce, evita lookup listing) finché
  // Phase 9 cutover. updateBoat con boat.id passa attraverso legacy diretto.
  async updateBoat(id: string, boatData: Partial<Boat>): Promise<Boat> {
    console.log('[boatService] Updating boat:', id, boatData);
    const data = await apiFetch(`/boats/${id}`, {
      method: 'PUT',
      body: JSON.stringify(boatData),
    });
    return transformBoat(data.data);
  },

  // Delete boat
  async deleteBoat(id: string): Promise<void> {
    await apiFetch(`/boats/${id}`, { method: 'DELETE' });
  },

  // Get available cities for dropdown
  async getAvailableCities(): Promise<string[]> {
    const data = await apiFetch('/boats/cities');
    return data.data || [];
  },

  // Get available zones for dropdown
  async getAvailableZones(): Promise<string[]> {
    const data = await apiFetch('/boats/zones');
    return data.data || [];
  },

  // Upload images to a boat (appends to existing)
  // Uses raw fetch because FormData must not have Content-Type set manually
  async uploadBoatImages(boatId: string, files: File[]): Promise<{ image_urls: string[]; uploaded: { url: string; thumb: string }[] }> {
    // Upload UNA foto per richiesta: evita che un batch sommi oltre il cap body
    // (foto smartphone 5-8MB l'una → più foto in un solo POST venivano troncate
    // a 10MB e perse). L'endpoint appende ogni immagine a image_urls.
    let result: { image_urls: string[]; uploaded: { url: string; thumb: string }[] } = { image_urls: [], uploaded: [] };
    for (const file of files) {
      const formData = new FormData();
      formData.append('images', file);

      const response = await fetch(`${API_URL}/boats/${boatId}/images`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to upload images');
      }

      const data = await response.json();
      result = data.data; // l'endpoint ritorna la lista image_urls cumulativa aggiornata
    }
    return result;
  },

  // Upload di UNA foto con progress reale (XHR: fetch non espone upload.onprogress).
  // Ritorna la lista image_urls cumulativa aggiornata dal server.
  uploadBoatImage(
    boatId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<{ image_urls: string[]; uploaded: { url: string; thumb: string }[] }> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('images', file);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/boats/${boatId}/images`);
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            onProgress?.(100);
            resolve(data.data);
          } catch { reject(new Error('Risposta non valida dal server')); }
        } else {
          let msg = 'Errore nel caricamento';
          try { msg = JSON.parse(xhr.responseText)?.message || msg; } catch { /* noop */ }
          reject(new Error(msg));
        }
      };
      xhr.onerror = () => reject(new Error('Errore di rete durante il caricamento'));
      xhr.send(formData);
    });
  },
};

export default boatService;
